/**
 * AlertsMap — Mapa mundial de regiones cloud con react-simple-maps.
 * Soporta:
 *  - Tooltip HTML flotante al hacer hover sobre cualquier punto
 *  - Filtro por proveedor (prop providerFilter): atenúa los demás
 *  - Zoom con rueda / pinch + arrastre
 *  - Marcadores rojos con pulso para regiones con alertas activas
 */

import React, { useState, useCallback, useMemo, useRef, memo } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup
} from 'react-simple-maps';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// ── Catálogo de regiones ─────────────────────────────────────────────────────
const CLOUD_REGIONS = [
  // AWS
  { key: 'us-east-1', lat: 38.9, lng: -77.45, provider: 'AWS' },
  { key: 'us-east-2', lat: 40.39, lng: -82.97, provider: 'AWS' },
  { key: 'us-west-1', lat: 37.34, lng: -121.89, provider: 'AWS' },
  { key: 'us-west-2', lat: 45.9, lng: -119.3, provider: 'AWS' },
  { key: 'ca-central-1', lat: 45.5, lng: -73.57, provider: 'AWS' },
  { key: 'ca-west-1', lat: 51.05, lng: -114.07, provider: 'AWS' },
  { key: 'mx-central-1', lat: 20.59, lng: -100.39, provider: 'AWS' },
  { key: 'sa-east-1', lat: -23.55, lng: -46.63, provider: 'AWS' },
  { key: 'eu-west-1', lat: 53.41, lng: -8.24, provider: 'AWS' },
  { key: 'eu-west-2', lat: 51.51, lng: -0.13, provider: 'AWS' },
  { key: 'eu-west-3', lat: 48.86, lng: 2.35, provider: 'AWS' },
  { key: 'eu-central-1', lat: 50.11, lng: 8.68, provider: 'AWS' },
  { key: 'eu-central-2', lat: 47.37, lng: 8.55, provider: 'AWS' },
  { key: 'eu-north-1', lat: 59.33, lng: 18.07, provider: 'AWS' },
  { key: 'eu-south-1', lat: 45.46, lng: 9.19, provider: 'AWS' },
  { key: 'eu-south-2', lat: 41.65, lng: -0.88, provider: 'AWS' },
  { key: 'me-south-1', lat: 26.07, lng: 50.55, provider: 'AWS' },
  { key: 'me-central-1', lat: 24.45, lng: 54.38, provider: 'AWS' },
  { key: 'il-central-1', lat: 32.08, lng: 34.78, provider: 'AWS' },
  { key: 'af-south-1', lat: -33.92, lng: 18.42, provider: 'AWS' },
  { key: 'ap-south-1', lat: 19.08, lng: 72.88, provider: 'AWS' },
  { key: 'ap-south-2', lat: 17.38, lng: 78.48, provider: 'AWS' },
  { key: 'ap-southeast-1', lat: 1.35, lng: 103.82, provider: 'AWS' },
  { key: 'ap-southeast-2', lat: -33.87, lng: 151.21, provider: 'AWS' },
  { key: 'ap-southeast-3', lat: -6.2, lng: 106.85, provider: 'AWS' },
  { key: 'ap-southeast-4', lat: -37.81, lng: 144.96, provider: 'AWS' },
  { key: 'ap-northeast-1', lat: 35.68, lng: 139.65, provider: 'AWS' },
  { key: 'ap-northeast-2', lat: 37.57, lng: 126.98, provider: 'AWS' },
  { key: 'ap-northeast-3', lat: 34.69, lng: 135.5, provider: 'AWS' },
  { key: 'ap-east-1', lat: 22.32, lng: 114.17, provider: 'AWS' },
  // Azure
  { key: 'eastus', lat: 37.37, lng: -79.8, provider: 'Azure' },
  { key: 'eastus2', lat: 36.6, lng: -78.4, provider: 'Azure' },
  { key: 'centralus', lat: 41.59, lng: -93.62, provider: 'Azure' },
  { key: 'northcentralus', lat: 41.88, lng: -87.63, provider: 'Azure' },
  { key: 'southcentralus', lat: 29.42, lng: -98.49, provider: 'Azure' },
  { key: 'westcentralus', lat: 41.14, lng: -104.8, provider: 'Azure' },
  { key: 'westus', lat: 37.78, lng: -122.42, provider: 'Azure' },
  { key: 'westus2', lat: 47.23, lng: -119.85, provider: 'Azure' },
  { key: 'westus3', lat: 33.45, lng: -112.07, provider: 'Azure' },
  { key: 'canadacentral', lat: 43.65, lng: -79.38, provider: 'Azure' },
  { key: 'canadaeast', lat: 46.81, lng: -71.21, provider: 'Azure' },
  { key: 'mexicocentral', lat: 20.7, lng: -100.2, provider: 'Azure' },
  { key: 'brazilsouth', lat: -23.35, lng: -46.4, provider: 'Azure' },
  { key: 'northeurope', lat: 53.35, lng: -6.26, provider: 'Azure' },
  { key: 'westeurope', lat: 52.37, lng: 4.9, provider: 'Azure' },
  { key: 'uksouth', lat: 51.5, lng: -0.9, provider: 'Azure' },
  { key: 'ukwest', lat: 51.48, lng: -3.18, provider: 'Azure' },
  { key: 'francecentral', lat: 48.6, lng: 2.6, provider: 'Azure' },
  { key: 'germanywestcentral', lat: 50.3, lng: 8.5, provider: 'Azure' },
  { key: 'switzerlandnorth', lat: 47.45, lng: 8.56, provider: 'Azure' },
  { key: 'norwayeast', lat: 59.91, lng: 10.75, provider: 'Azure' },
  { key: 'swedencentral', lat: 60.67, lng: 17.14, provider: 'Azure' },
  { key: 'polandcentral', lat: 52.23, lng: 21.01, provider: 'Azure' },
  { key: 'italynorth', lat: 45.66, lng: 9.4, provider: 'Azure' },
  { key: 'spaincentral', lat: 40.42, lng: -3.7, provider: 'Azure' },
  { key: 'uaenorth', lat: 25.2, lng: 55.27, provider: 'Azure' },
  { key: 'qatarcentral', lat: 25.28, lng: 51.53, provider: 'Azure' },
  { key: 'israelcentral', lat: 31.25, lng: 34.8, provider: 'Azure' },
  { key: 'southafricanorth', lat: -26.2, lng: 28.05, provider: 'Azure' },
  { key: 'centralindia', lat: 18.52, lng: 73.86, provider: 'Azure' },
  { key: 'southindia', lat: 13.08, lng: 80.27, provider: 'Azure' },
  { key: 'westindia', lat: 19.28, lng: 72.7, provider: 'Azure' },
  { key: 'eastasia', lat: 22.5, lng: 114.3, provider: 'Azure' },
  { key: 'southeastasia', lat: 1.55, lng: 103.7, provider: 'Azure' },
  { key: 'japaneast', lat: 35.85, lng: 139.8, provider: 'Azure' },
  { key: 'japanwest', lat: 34.85, lng: 135.35, provider: 'Azure' },
  { key: 'koreacentral', lat: 37.75, lng: 126.85, provider: 'Azure' },
  { key: 'koreasouth', lat: 35.18, lng: 129.08, provider: 'Azure' },
  { key: 'australiaeast', lat: -33.7, lng: 151.0, provider: 'Azure' },
  { key: 'australiasoutheast', lat: -37.95, lng: 144.75, provider: 'Azure' },
  // GCP
  { key: 'us-central1', lat: 41.26, lng: -95.86, provider: 'GCP' },
  { key: 'us-east1', lat: 33.2, lng: -80.01, provider: 'GCP' },
  { key: 'us-east4', lat: 39.05, lng: -77.5, provider: 'GCP' },
  { key: 'us-east5', lat: 39.96, lng: -83.0, provider: 'GCP' },
  { key: 'us-south1', lat: 32.78, lng: -96.8, provider: 'GCP' },
  { key: 'us-west1', lat: 45.6, lng: -121.2, provider: 'GCP' },
  { key: 'us-west2', lat: 34.05, lng: -118.24, provider: 'GCP' },
  { key: 'us-west3', lat: 40.76, lng: -111.89, provider: 'GCP' },
  { key: 'us-west4', lat: 36.17, lng: -115.14, provider: 'GCP' },
  { key: 'northamerica-northeast1', lat: 45.7, lng: -73.8, provider: 'GCP' },
  { key: 'northamerica-northeast2', lat: 43.85, lng: -79.6, provider: 'GCP' },
  { key: 'northamerica-south1', lat: 20.45, lng: -100.6, provider: 'GCP' },
  { key: 'southamerica-east1', lat: -23.75, lng: -46.85, provider: 'GCP' },
  { key: 'southamerica-west1', lat: -33.45, lng: -70.67, provider: 'GCP' },
  { key: 'europe-west1', lat: 50.45, lng: 3.82, provider: 'GCP' },
  { key: 'europe-west2', lat: 51.7, lng: -0.3, provider: 'GCP' },
  { key: 'europe-west3', lat: 49.9, lng: 8.85, provider: 'GCP' },
  { key: 'europe-west4', lat: 53.44, lng: 6.84, provider: 'GCP' },
  { key: 'europe-west6', lat: 47.2, lng: 8.7, provider: 'GCP' },
  { key: 'europe-west8', lat: 45.25, lng: 9.35, provider: 'GCP' },
  { key: 'europe-west9', lat: 49.05, lng: 2.2, provider: 'GCP' },
  { key: 'europe-west10', lat: 52.52, lng: 13.4, provider: 'GCP' },
  { key: 'europe-west12', lat: 45.07, lng: 7.69, provider: 'GCP' },
  { key: 'europe-central2', lat: 52.4, lng: 21.15, provider: 'GCP' },
  { key: 'europe-north1', lat: 60.57, lng: 27.19, provider: 'GCP' },
  { key: 'europe-southwest1', lat: 40.6, lng: -3.55, provider: 'GCP' },
  { key: 'me-west1', lat: 32.25, lng: 34.9, provider: 'GCP' },
  { key: 'me-central1', lat: 25.45, lng: 51.4, provider: 'GCP' },
  { key: 'me-central2', lat: 26.43, lng: 50.1, provider: 'GCP' },
  { key: 'africa-south1', lat: -26.35, lng: 28.2, provider: 'GCP' },
  { key: 'asia-south1', lat: 19.25, lng: 73.05, provider: 'GCP' },
  { key: 'asia-south2', lat: 28.61, lng: 77.21, provider: 'GCP' },
  { key: 'asia-southeast1', lat: 1.2, lng: 103.95, provider: 'GCP' },
  { key: 'asia-southeast2', lat: -6.35, lng: 107.0, provider: 'GCP' },
  { key: 'asia-east1', lat: 25.03, lng: 121.57, provider: 'GCP' },
  { key: 'asia-east2', lat: 22.15, lng: 114.05, provider: 'GCP' },
  { key: 'asia-northeast1', lat: 35.5, lng: 139.45, provider: 'GCP' },
  { key: 'asia-northeast2', lat: 34.55, lng: 135.65, provider: 'GCP' },
  { key: 'asia-northeast3', lat: 37.4, lng: 127.1, provider: 'GCP' },
  { key: 'australia-southeast1', lat: -34.05, lng: 151.4, provider: 'GCP' },
  { key: 'australia-southeast2', lat: -37.65, lng: 145.15, provider: 'GCP' },
  // Huawei Cloud
  { key: 'cn-north-1', lat: 39.9, lng: 116.4, provider: 'Huawei' },
  { key: 'cn-north-4', lat: 40.2, lng: 116.7, provider: 'Huawei' },
  { key: 'cn-east-2', lat: 31.23, lng: 121.47, provider: 'Huawei' },
  { key: 'cn-east-3', lat: 31.5, lng: 121.15, provider: 'Huawei' },
  { key: 'cn-south-1', lat: 23.13, lng: 113.26, provider: 'Huawei' },
  { key: 'cn-southwest-2', lat: 26.65, lng: 106.63, provider: 'Huawei' },
  { key: 'ap-southeast-1', lat: 22.4, lng: 114.35, provider: 'Huawei' },
  { key: 'ap-southeast-2', lat: 13.76, lng: 100.5, provider: 'Huawei' },
  { key: 'ap-southeast-3', lat: 1.29, lng: 103.6, provider: 'Huawei' },
  { key: 'ap-southeast-4', lat: -6.1, lng: 106.7, provider: 'Huawei' },
  { key: 'la-north-2', lat: 19.43, lng: -99.13, provider: 'Huawei' },
  { key: 'la-south-2', lat: -33.6, lng: -70.8, provider: 'Huawei' },
  { key: 'sa-brazil-1', lat: -23.4, lng: -46.3, provider: 'Huawei' },
  { key: 'af-south-1', lat: -26.1, lng: 27.9, provider: 'Huawei' },
  { key: 'tr-west-1', lat: 41.01, lng: 28.98, provider: 'Huawei' },
  { key: 'me-east-1', lat: 24.71, lng: 46.68, provider: 'Huawei' },
];

const PROVIDER_COLORS = {
  AWS: '#fb923c',
  Azure: '#38bdf8',
  GCP: '#34d399',
  Huawei: '#f87171',
};
const ALERT_COLOR = '#dc2626';
const DEFAULT_POSITION = { coordinates: [15, 25], zoom: 1 };

const regionTextContains = (regionText, regionKey) => {
  if (!regionText || !regionKey) return false;
  const escapedKey = regionKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-z0-9])${escapedKey}(?=$|[^a-z0-9])`, 'i').test(regionText);
};

const WorldGeographies = memo(() => (
  <Geographies geography={GEO_URL}>
    {({ geographies }) =>
      geographies.map((geo) => (
        <Geography
          key={geo.rsmKey}
          geography={geo}
          fill="rgba(139,92,246,0.14)"
          stroke="rgba(167,139,250,0.35)"
          strokeWidth={0.4}
          style={{
            default: { outline: 'none' },
            hover: { outline: 'none', fill: 'rgba(139,92,246,0.22)' },
            pressed: { outline: 'none' },
          }}
        />
      ))
    }
  </Geographies>
));

export default function AlertsMap({
  alertsByRegion = {},
  providerAlerts = [],
  loading = false,
  onRegionClick = null,
  providerFilter = null, // proveedor actualmente filtrado (p.ej. "AWS")
}) {
  const containerRef = useRef(null);
  const [position, setPosition] = useState(DEFAULT_POSITION);
  // tooltip: { x, y, key, provider, count|null }
  const [tooltip, setTooltip] = useState(null);

  const handleMoveEnd = useCallback((pos) => setPosition(pos), []);
  const zoomIn = () => setPosition((p) => ({ ...p, zoom: Math.min(p.zoom * 1.5, 8) }));
  const zoomOut = () => setPosition((p) => ({ ...p, zoom: Math.max(p.zoom / 1.5, 1) }));
  const resetView = () => setPosition(DEFAULT_POSITION);

  // Índice case-insensitive de alertas por región
  const alertsByRegionNorm = useMemo(() => {
    const norm = {};
    Object.entries(alertsByRegion || {}).forEach(([k, v]) => {
      norm[k.toLowerCase()] = v;
    });
    return norm;
  }, [alertsByRegion]);

  const getAlertCount = (regionKey, provider) => {
    if (providerAlerts.length > 0) {
      return providerAlerts.filter((alert) =>
        alert.provider === provider && regionTextContains(alert.region, regionKey)
      ).length;
    }

    const value = alertsByRegionNorm[regionKey.toLowerCase()];
    if (!value) return 0;
    if (typeof value === 'number') return value;
    return value.count ?? value.alert_count ?? 0;
  };

  const totalAlertRegions = useMemo(
    () => new Set(
      CLOUD_REGIONS
        .filter((region) => getAlertCount(region.key, region.provider) > 0)
        .map((region) => `${region.provider}-${region.key}`)
    ).size,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alertsByRegionNorm, providerAlerts]
  );

  // Actualizar posición del tooltip al mover el mouse sobre el contenedor
  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTooltip((prev) =>
      prev ? { ...prev, x: e.clientX - rect.left, y: e.clientY - rect.top } : null
    );
  }, []);

  const showTooltip = useCallback((e, key, provider, count) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      key,
      provider,
      count,
    });
  }, []);

  const hideTooltip = useCallback(() => setTooltip(null), []);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden rounded-xl border border-violet-500/20 bg-[#120726]"
      onMouseMove={handleMouseMove}
      onMouseLeave={hideTooltip}
    >
      {/* Gradiente de fondo */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(124,58,237,0.18),transparent_45%),radial-gradient(circle_at_bottom_left,rgba(34,211,238,0.08),transparent_40%)]" />

      {loading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#120726]/70 backdrop-blur-sm">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-violet-400/30 border-t-violet-300" />
        </div>
      )}

      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{ scale: 165 }}
        width={980}
        height={480}
        style={{ width: '100%', height: '100%' }}
      >
        <ZoomableGroup
          center={position.coordinates}
          zoom={position.zoom}
          minZoom={1}
          maxZoom={8}
          onMoveEnd={handleMoveEnd}
        >
          <WorldGeographies />

          {CLOUD_REGIONS.map((region) => {
            const { key, provider, lat, lng } = region;
            const markerId = `${provider}-${key}`;
            const count = getAlertCount(key, provider);
            const hasAlert = count > 0;
            const markerScale = 1 / Math.sqrt(position.zoom);

            // Atenuar si hay filtro de proveedor y este no coincide
            const dimmed = providerFilter && providerFilter !== provider;
            const opacity = dimmed ? 0.15 : 1;

            if (!hasAlert) {
              const color = PROVIDER_COLORS[provider] || '#22d3ee';
              return (
                <Marker
                  key={markerId}
                  coordinates={[lng, lat]}
                  onMouseEnter={(e) => showTooltip(e, key, provider, null)}
                  onMouseLeave={hideTooltip}
                >
                  <circle
                    r={dimmed ? 2 * markerScale : 3 * markerScale}
                    fill={color}
                    fillOpacity={opacity * 0.9}
                    stroke="rgba(255,255,255,0.4)"
                    strokeWidth={0.5 * markerScale}
                    style={{ cursor: 'default', transition: 'fill-opacity 0.2s' }}
                  />
                </Marker>
              );
            }

            // Región con alertas — marcador rojo con doble pulso y conteo
            const color = ALERT_COLOR;
            return (
              <Marker
                key={markerId}
                coordinates={[lng, lat]}
                onMouseEnter={(e) => showTooltip(e, key, provider, count)}
                onMouseLeave={hideTooltip}
                onClick={() => onRegionClick && onRegionClick(key)}
              >
                {/* Anillo exterior de pulso — más grande */}
                <circle
                  r={20 * markerScale}
                  fill={color}
                  fillOpacity={opacity * 0.08}
                  className="animate-ping"
                  style={{ transformOrigin: 'center', pointerEvents: 'none', animationDuration: '1.8s' }}
                />
                {/* Anillo medio de pulso */}
                <circle
                  r={13 * markerScale}
                  fill={color}
                  fillOpacity={opacity * 0.18}
                  className="animate-ping"
                  style={{ transformOrigin: 'center', pointerEvents: 'none', animationDuration: '1.2s', animationDelay: '0.3s' }}
                />
                {/* Halo estático */}
                <circle
                  r={12 * markerScale}
                  fill={color}
                  fillOpacity={opacity * 0.3}
                  stroke="#fef2f2"
                  strokeWidth={2 * markerScale}
                  strokeOpacity={opacity * 0.95}
                  style={{ opacity }}
                />
                {/* Núcleo */}
                <circle
                  r={7 * markerScale}
                  fill={color}
                  stroke="#fff"
                  strokeWidth={1.5 * markerScale}
                  style={{ cursor: 'pointer', opacity }}
                />
                {/* Conteo dentro del núcleo */}
                <text
                  textAnchor="middle"
                  y={2 * markerScale}
                  style={{
                    fontSize: 7 * markerScale,
                    fontWeight: 800,
                    fill: '#fff',
                    fontFamily: 'ui-sans-serif, system-ui',
                    pointerEvents: 'none',
                    opacity,
                  }}
                >
                  {count}
                </text>
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      {/* ── Tooltip HTML flotante ─────────────────────────────────────────── */}
      {tooltip && (
        <div
          className="pointer-events-none absolute z-30 rounded-xl border border-violet-500/30 bg-[#160a2e]/95 px-3 py-2 shadow-xl backdrop-blur-md"
          style={{
            left: tooltip.x + 14,
            top: tooltip.y - 10,
            // Evitar que se salga del borde derecho
            transform: tooltip.x > 680 ? 'translateX(-110%)' : undefined,
          }}
        >
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{
                backgroundColor: tooltip.count ? ALERT_COLOR : PROVIDER_COLORS[tooltip.provider] || '#a78bfa',
              }}
            />
            <span className="text-xs font-bold text-violet-50">{tooltip.key}</span>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span
              className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
              style={{
                color: PROVIDER_COLORS[tooltip.provider] || '#a78bfa',
                background: `${PROVIDER_COLORS[tooltip.provider] || '#a78bfa'}20`,
              }}
            >
              {tooltip.provider}
            </span>
            {tooltip.count ? (
              <span className="text-[10px] font-bold" style={{ color: ALERT_COLOR }}>
                ⚠ {tooltip.count} alerta{tooltip.count !== 1 ? 's' : ''}
              </span>
            ) : (
              <span className="text-[10px] text-emerald-400/80">Operativa</span>
            )}
          </div>
        </div>
      )}

      {/* ── Controles de zoom ─────────────────────────────────────────────── */}
      <div className="absolute right-3 top-3 z-10 flex flex-col gap-1.5">
        {[
          { icon: ZoomIn, action: zoomIn, title: 'Acercar' },
          { icon: ZoomOut, action: zoomOut, title: 'Alejar' },
          { icon: RotateCcw, action: resetView, title: 'Restablecer vista' },
        ].map(({ icon: Icon, action, title }) => (
          <button
            key={title}
            onClick={action}
            className="rounded-lg border border-violet-300/40 bg-violet-500/25 p-1.5 text-violet-100 backdrop-blur-sm transition hover:bg-violet-500/45"
            title={title}
          >
            <Icon size={15} />
          </button>
        ))}
      </div>

      {/* ── Leyenda ───────────────────────────────────────────────────────── */}
      <div className="absolute bottom-3 left-3 z-10 rounded-xl border border-violet-500/25 bg-[#1a0d36]/90 px-3 py-2 backdrop-blur-md">
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-violet-300">
          Leyenda
        </p>
        <div className="flex flex-col gap-1 text-[10px] text-violet-100/80">
          {Object.entries(PROVIDER_COLORS).map(([prov, color]) => (
            <span
              key={prov}
              className="flex items-center gap-1.5"
              style={{ opacity: providerFilter && providerFilter !== prov ? 0.4 : 1 }}
            >
              <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
              {prov} (operativa)
            </span>
          ))}
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ALERT_COLOR }} />
            Región con alertas
          </span>
        </div>
      </div>

      {/* ── Estado / info ─────────────────────────────────────────────────── */}
      <div className="absolute bottom-3 right-3 z-10 rounded-xl border border-violet-500/25 bg-[#1a0d36]/90 px-3 py-2 text-[10px] text-violet-100/70 backdrop-blur-md">
        {providerFilter ? (
          <span style={{ color: PROVIDER_COLORS[providerFilter] || '#c4b5fd' }}>
            Filtrando: {providerFilter}
          </span>
        ) : totalAlertRegions > 0 ? (
          `${totalAlertRegions} región(es) con alertas activas`
        ) : (
          'Todas las regiones operativas'
        )}
        <span className="ml-2 text-violet-300/50">· Rueda: zoom · Arrastrar: mover</span>
      </div>
    </div>
  );
}
